import { useState, useEffect, useCallback, useRef } from "react"
import type { AuditHistoryDocument, AuditResponse, WorkOrderAuditGroup } from "@/types/audit"
import { useApi } from "@/lib/api"
import { logger } from "@/lib/logger"

interface UseAuditHistoryReturn {
  records: AuditHistoryDocument[]
  groupedRecords: WorkOrderAuditGroup[]
  loading: boolean
  error: string | null
  totalCount: number
  currentPage: number
  hasMore: boolean
  searchTerm: string
  loadHistory: (itemId: string, entityType?: string, reset?: boolean) => Promise<void>
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  setSearchTerm: (term: string) => void
  clearError: () => void
}

export function useAuditHistory(
  itemId?: string,
  entityType?: string,
  pageSize: number = 20
): UseAuditHistoryReturn {
  const [records, setRecords] = useState<AuditHistoryDocument[]>([])
  const [groupedRecords, setGroupedRecords] = useState<WorkOrderAuditGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  
  const { workflowApi } = useApi()
  
  // Use ref to track if we've already loaded data for this itemId
  const loadedItemIdRef = useRef<string | null>(null)

  const loadHistory = useCallback(
    async (targetItemId: string, targetEntityType?: string, reset: boolean = false) => {
      if (!targetItemId) return

      try {
        setLoading(true)
        setError(null)

        const page = reset ? 1 : currentPage
        
        // V2: Call new workflow endpoint
        const params = new URLSearchParams()
        if (targetEntityType && targetEntityType !== 'Assignment') {
          params.append('entityType', targetEntityType)
        }
        // Note: searchTerm is handled client-side for better performance
        if (page > 1) {
          params.append('page', page.toString())
        }
        if (pageSize !== 20) {
          params.append('pageSize', pageSize.toString())
        }
        
        const queryString = params.toString()
        const url = `/api/workflow/v2/audit-history/${targetItemId}${queryString ? '?' + queryString : ''}`
        
        const response = await workflowApi.get<WorkOrderAuditGroup[]>(url)

        // Handle grouped response
        if (Array.isArray(response)) {
          const grouped = response as WorkOrderAuditGroup[]
          setGroupedRecords(grouped)
          
          // Flatten groups to maintain backward compatibility with existing UI
          const flattenedRecords = grouped.flatMap(group => group.changes)
          
          if (reset) {
            setRecords(flattenedRecords)
            setCurrentPage(1)
          } else {
            setRecords(prev => [...prev, ...flattenedRecords])
          }
          
          setTotalCount(flattenedRecords.length)
          setHasMore(false) // V2 endpoint returns all results at once
        }
      } catch (err) {
        // Don't set error state - just log it and show empty results

        if (err instanceof Error) logger.error(err, { hook: 'useAuditHistory', itemId: targetItemId })
        if (reset) {
          setRecords([])
          setGroupedRecords([])
          setTotalCount(0)
          setHasMore(false)
        }
      } finally {
        setLoading(false)
      }
    },
    [currentPage, pageSize, workflowApi]
  )

  const loadMore = useCallback(async () => {
    if (!itemId || !hasMore || loading) return

    setCurrentPage(prev => prev + 1)
    await loadHistory(itemId, entityType, false)
  }, [itemId, entityType, hasMore, loading, loadHistory])

  const refresh = useCallback(async () => {
    if (!itemId) return
    await loadHistory(itemId, entityType, true)
  }, [itemId, entityType, loadHistory])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Load initial history when itemId changes (only once)
  useEffect(() => {
    if (itemId && itemId !== loadedItemIdRef.current) {
      loadedItemIdRef.current = itemId
      loadHistory(itemId, entityType, true)
    }
  }, [itemId, entityType, loadHistory])

  // Search is now handled client-side in the component for better performance
  // No need to reload data when searchTerm changes

  return {
    records,
    groupedRecords,
    loading,
    error,
    totalCount,
    currentPage,
    hasMore,
    searchTerm,
    loadHistory,
    loadMore,
    refresh,
    setSearchTerm,
    clearError,
  }
}
